import type { AlertColor, SnackbarOrigin } from '@mui/material';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { ALL_TYPES } from '../../constants/snackbar';

import { ThrowError } from '../../helpers/errors';
import { getUniqueAppId } from '../../helpers/string';

export type SnackBarMessageType = {
  key: string;
  message: string;
  duration?: number;
  variant: AlertColor;
  onClose?: () => void;
  dismissed?: boolean;
  position?: SnackbarOrigin;
};

type State = {
  notifications: SnackBarMessageType[];
  palette: {
    error: {
      main: string;
      contrastText: string;
    };
    warning: {
      main: string;
      contrastText: string;
    };
    success: {
      main: string;
      contrastText: string;
    };
    info: {
      main: string;
      contrastText: string;
    };
  };
};

const initialState: State = {
  notifications: [],
  palette: {
    error: {
      main: '#F5605B',
      contrastText: '#000',
    },
    warning: {
      main: '#FFA234',
      contrastText: '#000',
    },
    success: {
      main: '#57AF48',
      contrastText: '#000',
    },
    info: {
      main: '#929EC8',
      contrastText: '#000',
    },
  },
};

/** https://immerjs.github.io/immer/update-patterns/ */
export const slice = createSlice({
  name: 'snackbar',
  initialState,
  reducers: {
    notifyAction: (state, action: PayloadAction<Omit<SnackBarMessageType, 'key'> & { key?: string }>) => {
      if (!ALL_TYPES.includes(action.payload.variant)) {
        ThrowError('Wrong variant of the notify message');
      }

      state.notifications.push({
        key: action.payload.key ?? getUniqueAppId(),
        variant: action.payload.variant,
        message: action.payload.message,
        duration: action.payload.duration ?? 6000,
        onClose: action.payload.onClose,
        position: action.payload.position,
        dismissed: false,
      });
    },
    closeNotifyAction: (state, action) => {
      state.notifications.forEach((notification) => {
        // eslint-disable-next-line no-param-reassign
        notification.dismissed = !action.payload || notification.key === action.payload;
      });
    },
    removeSnackbarAction: (state, action) => {
      const index = state.notifications.findIndex((notification) => notification.key === action.payload);

      if (index !== -1) {
        state.notifications.splice(index, 1);
      }
    },
  },
});

export const { notifyAction, closeNotifyAction, removeSnackbarAction } = slice.actions;
